import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { DIALOG_VARIANTS, type DialogVariant } from './dialogVariants';
import { lockBodyScroll, stopScrollChain } from '../../utils/lockBodyScroll';

export type DesignedModalShellProps = {
  open: boolean;
  variant?: DialogVariant;
  attentionLabel?: string;
  title: string;
  subtitle?: string;
  onClose?: () => void;
  closeDisabled?: boolean;
  zIndex?: number;
  maxWidth?: number | string;
  role?: 'dialog' | 'alertdialog';
  ariaLabelledBy?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
};

const DesignedModalShell: React.FC<DesignedModalShellProps> = ({
  open,
  variant = 'primary',
  attentionLabel,
  title,
  subtitle,
  onClose,
  closeDisabled = false,
  zIndex = 10050,
  maxWidth = 480,
  role = 'dialog',
  ariaLabelledBy = 'designed-modal-title',
  children,
  footer,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    return lockBodyScroll();
  }, [open]);

  if (!open) return null;

  const v = DIALOG_VARIANTS[variant];
  const Icon = v.Icon;

  return (
    <div
      className="modal-overlay designed-modal-overlay"
      style={{ zIndex }}
      onClick={() => !closeDisabled && onClose?.()}
      onWheel={stopScrollChain}
      onTouchMove={stopScrollChain}
      role="presentation"
    >
      <div
        className="modal-content designed-modal-content"
        style={{
          maxWidth,
          padding: 0,
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.28)',
        }}
        onClick={(e) => e.stopPropagation()}
        onWheel={stopScrollChain}
        onTouchMove={stopScrollChain}
        role={role}
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
      >
        <div
          style={{
            background: v.headerGradient,
            borderBottom: `1px solid ${v.headerBorder}`,
            padding: '1.25rem 1.5rem',
            display: 'flex',
            gap: '1rem',
            alignItems: 'flex-start',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              background: v.iconBg,
              color: 'white',
              padding: '0.65rem',
              borderRadius: '14px',
              flexShrink: 0,
              boxShadow: v.iconShadow,
            }}
          >
            <Icon size={26} strokeWidth={2.25} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: v.attentionColor,
              }}
            >
              {attentionLabel ?? v.attentionLabel}
            </p>
            <h3
              id={ariaLabelledBy}
              style={{ margin: '0.35rem 0 0', fontSize: '1.15rem', fontWeight: 800, color: v.titleColor, lineHeight: 1.3 }}
            >
              {title}
            </h3>
            {subtitle && (
              <p style={{ margin: '0.45rem 0 0', fontSize: '0.85rem', color: v.titleColor, opacity: 0.85, fontWeight: 600 }}>
                {subtitle}
              </p>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              disabled={closeDisabled}
              aria-label="Close"
              style={{
                background: 'rgba(255,255,255,0.65)',
                border: 'none',
                borderRadius: '8px',
                padding: '0.35rem',
                cursor: closeDisabled ? 'not-allowed' : 'pointer',
                color: v.closeColor,
              }}
            >
              <X size={20} />
            </button>
          )}
        </div>

        <div className="designed-modal-body">{children}</div>

        {footer && (
          <div className="designed-modal-footer">{footer}</div>
        )}
      </div>
    </div>
  );
};

export default DesignedModalShell;